import React from 'react';

import { EvTableColumn } from '@evinced-private/ui-common';

import { MfaComponentDetailsBlock } from 'src/apps/mfa/components/MfaComponentDetailsBlock';
import {
	IMfaIssueElementInfo,
	IMfaSessionIssue
} from 'src/apps/mfa/types/MfaDashboardServiceTypes';
import { stringCellFormatter } from 'src/common/components/ev-table/cell-formatters/string-cell-formatter/stringCellFormatter';
import {
	ExternalTicketFormatter,
	IExternalTicket
} from 'src/common/components/external-ticket-formatter/ExternalTicketFormatter';
import { IssueElementInfoFormatter } from 'src/common/components/issue-element-info-formatter/IssueElementInfoFormatter';
import { MobileScreenshotCellFormatter } from 'src/common/components/mobile-screenshot-cell-formatter/MobileScreenshotCellFormatter';
import { ScanTimeFormatter } from 'src/common/components/scan-time-formatter/ScanTimeFormatter';
import { SeverityCellFormatter } from 'src/common/components/severity-cell-formatter/SeverityCellFormatter';
import { ShowIssueDetailsCellFormatter } from 'src/common/components/show-details-cell-formatter/ShowIssueDetailsCellFormatter';

export const getMfaAllIssuesColumns = (
	runId: string,
	scanCreatedAtMap: Map<string, string>
): EvTableColumn[] => [
	{
		dataField: 'type',
		text: 'Issue type',
		style: { width: '150px' },
		headerStyle: { width: '150px' },
		formatter: stringCellFormatter,
		sort: true
	},
	{
		dataField: 'severity',
		text: 'Severity',
		style: { width: '120px' },
		headerStyle: { width: '120px' },
		sort: true,
		formatter: (severity: string) => <SeverityCellFormatter severity={severity} />
	},
	{
		dataField: 'scanName',
		text: 'Scan',
		style: { width: '140px' },
		headerStyle: { width: '140px' },
		sort: true,
		formatter: (scanName: string, issue: IMfaSessionIssue) => {
			const scanCreatedAt = scanCreatedAtMap.get(issue.scanId) ?? '';
			return <ScanTimeFormatter runTime={scanCreatedAt} scanName={scanName} />;
		}
	},
	{
		dataField: 'elementInfo',
		text: 'Element description',
		style: { width: '300px' },
		headerStyle: { width: '300px' },
		formatter: (elementInfo: IMfaIssueElementInfo[], issue: IMfaSessionIssue) => (
			<IssueElementInfoFormatter
				elementInfo={elementInfo}
				nodeNumber={issue.shortNodeId}
				withTooltip={true}
			/>
		)
	},
	{
		dataField: 'taskManagerSystemTickets',
		text: 'Ticket',
		style: { width: '120px' },
		headerStyle: { width: '120px' },
		formatter: (tickets: IExternalTicket[]) => <ExternalTicketFormatter tickets={tickets} />
	},
	{
		dataField: 'screenshotUrl',
		text: 'Screenshot',
		style: { width: '182px', minWidth: '182px' },
		headerStyle: { width: '182px' },
		formatter: (cell, issue: IMfaSessionIssue) => (
			<MobileScreenshotCellFormatter
				cellDescriptor={issue.type}
				screenshotDetails={{
					pageScreenshotUrl: issue.screenshotUrl,
					boundingBoxes: [issue.boundingBox]
				}}
				viewportWidth={issue.viewportWidth}
				viewportHeight={issue.viewportHeight}
			/>
		)
	},
	{
		dataField: '',
		text: '',
		style: { width: '60px' },
		headerStyle: { width: '60px' },
		formatter: (cell, issue: IMfaSessionIssue) => {
			const renderDetailsBlock = (): JSX.Element => {
				const scanCreatedAt = scanCreatedAtMap.get(issue.scanId) ?? '';
				return (
					<MfaComponentDetailsBlock
						issueId={issue.id}
						runId={runId}
						scanName={issue.scanName}
						scanCreatedAt={scanCreatedAt}
						issueNumber={issue.shortNodeId}
					/>
				);
			};

			return (
				<ShowIssueDetailsCellFormatter
					renderDetailsBlock={renderDetailsBlock}
					infoTitle="See Issue details"
					className="mobile-details-popup"
				/>
			);
		}
	}
];
